import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Container,
    Form,
    Button,
    Alert,
    Row,
    Col,
    Card,
    Modal,
} from 'react-bootstrap';
import Layout from "../../../layout/default";
import Swal from 'sweetalert2'; // Added SweetAlert import

const CustomerRegisterWithReferral = () => {
    const { id } = useParams(); // Extract referral code from the URL
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [showTermsModal, setShowTermsModal] = useState(false);
    const [customerData, setCustomerData] = useState({
        email: '',
        userName: '',
        password: '',
        confirmPassword: '',
        referralCode: id || '', // Set referral code from URL
        lastName: '',
        middlename: '',
        phone1: '',
        phone2: '',
        aadharNumber: '',
        minimumOrder: '',
        comissionId: '',
        pincode: '',
        user_type: 1, // Default user_type
    });

    const [minimumOrders, setMinimumOrders] = useState([]); // Store fetched minimum_order and IDs
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    // Fetch minimum orders and corresponding commission IDs
    useEffect(() => {
        const fetchMinimumOrders = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_HOST}api/comissions`);
                setMinimumOrders(response.data); // Set fetched data
            } catch (err) {
                console.error('Error fetching minimum orders:', err);
                setError('Failed to fetch minimum order options.');
            }
        };

        fetchMinimumOrders();
    }, []);

    const handleTermsChange = (e) => {
        if (e.target.checked) {
            setShowTermsModal(true);
        } else {
            setTermsAccepted(false);
        }
    };
    const handleModalClose = (accepted) => {
        setShowTermsModal(false);
        setTermsAccepted(accepted);
    };
    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === 'minimumOrder') {
            // Find the corresponding comissionId for the selected minimumOrder
            const selectedOrder = minimumOrders.find(
                (order) => order.minimum_order === parseFloat(value)
            );

            setCustomerData({
                ...customerData,
                minimumOrder: value,
                comissionId: selectedOrder ? selectedOrder.id : '',
            });
        } else {
            setCustomerData({ ...customerData, [name]: value });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        // Validation checks
        const { email, userName, password, confirmPassword, phone1 } = customerData;

        if (!email || !userName || !password || !confirmPassword) {
            setError('Please fill in all required fields.');
            return;
        }
        if (!/^\d{10}$/.test(phone1)) {
            Swal.fire({
                icon: 'error',
                title: 'Invalid Input',
                text: 'Primary phone number must be 10 digits.',
            });
            return false;
        }

        if (password.length < 6) {
            Swal.fire({
                icon: 'error',
                title: 'Invalid Input',
                text: 'Password must be at least 6 characters long.',
            });
            return false;
        }
        if (password !== confirmPassword) {
            Swal.fire({
                icon: 'error',
                title: 'Invalid Input',
                text: 'Password and Confirm Password must match.',
            });
            return false;
        }

        if (!customerData.referralCode || !customerData.minimumOrder || !customerData.comissionId) {
            setError('Referral code, minimum order, and commission ID are required.');
            return;
        }

        if (!/^\d{6}$/.test(customerData.pincode)) {
            setError('Pincode must be a valid 6-digit number.');
            return;
        }

        if (!/^\d{12}$/.test(customerData.aadharNumber)) {
            setError('Aadhar number must be a valid 12-digit number.');
            return;
        }

        setLoading(true);

        try {
            const response = await axios.post(`${process.env.REACT_APP_HOST}api/register-referral`, {
                email: customerData.email,
                user_name: customerData.userName,
                password: customerData.password,
                c_password: customerData.confirmPassword,
                referral_code: customerData.referralCode,
                last_name: customerData.lastName,
                middle_name: customerData.middlename,
                phone_1: customerData.phone1,
                phone2: customerData.phone2,
                aadhar_number: customerData.aadharNumber,
                minimum_order: parseFloat(customerData.minimumOrder),
                comission_id: customerData.comissionId,
                pincode: customerData.pincode,
                user_type: customerData.user_type,
            });

            if (response.status === 200) {
                Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: 'Successfully registered! Redirecting to login page...',
                    timer: 2000,
                    timerProgressBar: true,
                    showConfirmButton: false,
                });
                setTimeout(() => {
                    navigate('/shop/auths/login');
                }, 2000);
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Unexpected response status: ' + response.status,
                });
            }
        } catch (err) {
            let errorMessage = 'An unexpected error occurred during registration. Please try again.';

            if (err.response && err.response.data) {
                // Handle specific backend error messages
                if (err.response.data.message === 'Email already exists.') {
                    errorMessage = 'The email address is already registered. Please use a different email.';
                } else {
                    errorMessage = err.response.data.message || errorMessage;

                    // Handle validation errors
                    if (err.response.data.data) {
                        const validationErrors = Object.values(err.response.data.data).flat().join(' ');
                        errorMessage = validationErrors;
                    }
                }
            }

            Swal.fire({
                icon: 'error',
                title: 'Registration Failed',
                text: errorMessage,
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Layout title="Customer Registration">
            <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
                <Row className="w-100 justify-content-center">
                    <Col xs={12} md={6} lg={5}>
                        <Card className="p-4 shadow-sm">
                            <Card.Body>
                                <h2 className="mb-4 text-center">Affiliator Registration</h2>
                                {error && <Alert variant="danger">{error}</Alert>}
                                {success && <Alert variant="success">{success}</Alert>}
                                <Form onSubmit={handleSubmit}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Email</Form.Label>
                                        <Form.Control
                                            type="email"
                                            name="email"
                                            value={customerData.email}
                                            onChange={handleChange}
                                            placeholder="Enter email"
                                            required
                                        />
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label>First name</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="userName"
                                            value={customerData.userName}
                                            onChange={handleChange}
                                            placeholder="Enter first name"
                                            required
                                        />
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label>Middle Name</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="middlename"
                                            value={customerData.middlename}
                                            onChange={handleChange}
                                            placeholder="Enter last name"
                                        />
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label>Last Name</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="lastName"
                                            value={customerData.lastName}
                                            onChange={handleChange}
                                            placeholder="Enter last name"
                                            required
                                        />
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label>Password</Form.Label>
                                        <Form.Control
                                            type="password"
                                            name="password"
                                            value={customerData.password}
                                            onChange={handleChange}
                                            placeholder="Enter password"
                                            required
                                        />
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label>Confirm Password</Form.Label>
                                        <Form.Control
                                            type="password"
                                            name="confirmPassword"
                                            value={customerData.confirmPassword}
                                            onChange={handleChange}
                                            placeholder="Confirm password"
                                            required
                                        />
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label>Phone 1</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="phone1"
                                            value={customerData.phone1}
                                            onChange={handleChange}
                                            placeholder="Enter phone number"
                                            required
                                        />
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label>Phone 2 (Optional)</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="phone2"
                                            value={customerData.phone2}
                                            onChange={handleChange}
                                            placeholder="Enter alternate phone number"
                                        />
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label>Aadhar Number</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="aadharNumber"
                                            value={customerData.aadharNumber}
                                            onChange={handleChange}
                                            placeholder="Enter Aadhar number"
                                            required
                                        />
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label>Pincode</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="pincode"
                                            value={customerData.pincode}
                                            onChange={handleChange}
                                            placeholder="Enter pincode"
                                            required
                                        />
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label>Minimum Order</Form.Label>
                                        <Form.Select
                                            name="minimumOrder"
                                            value={customerData.minimumOrder}
                                            onChange={handleChange}
                                            required
                                        >
                                            <option value="">Select Minimum Order</option>
                                            {minimumOrders.map((order) => (
                                                <option key={order.id} value={order.minimum_order}>
                                                    {order.minimum_order}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                    <Form.Group className="mb-3">
                                        <Form.Check
                                            type="checkbox"
                                            id="terms"
                                            label="I agree to the Terms and Conditions"
                                            checked={termsAccepted}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setShowTermsModal(true); // Show modal when checkbox is checked
                                                } else {
                                                    setTermsAccepted(false); // Uncheck if user unchecks the box
                                                }
                                            }}
                                            required
                                        />
                                    </Form.Group>
                                    <Button className="register-button w-100" variant="primary" type="submit"  disabled={loading || !termsAccepted}>
                                        {loading ? 'Registering...' : 'Register'}
                                    </Button>
                                </Form>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>
            {/* Terms and Conditions Modal */}
            <Modal show={showTermsModal} onHide={() => handleModalClose(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Terms and Conditions</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        Rules and Regulation for Affiliate:
                        <br />
                        Mandatory Purchase Of Grocery only Rs.3000/
                        <br />
                        Obliged to pay AMC (Rs. 500/per year)
                        <br />
                        No commission or bonus or any other incomes, if so... nothing will
                        be given, if Mandatory Purchase not done for the month.
                        <br />
                        3 consecutive non-purchasing leads to terminate the ID, and it
                        leads to get No Commission, bonus or any other incomes if so.
                        <br />
                        Customer is responsible to check the quality and quantity before
                        giving OTP to the vendor or supplier.
                        <br />
                        OTP Confirmation: Verifies Delivery with right quantity and
                        quality, also authorizes to deduct ordered products' whole amount
                        from the wallet.
                        <br />
                        No refund no cancellation.
                        <br />
                        Terms and conditions may change anytime without prior notice.
                        <br />
                        Subject to Hubli jurisdiction.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => handleModalClose(false)}>
                        Decline
                    </Button>
                    <Button variant="primary" onClick={() => handleModalClose(true)}>
                        Accept
                    </Button>
                </Modal.Footer>
            </Modal>
            <style jsx>{`
      @media (max-width: 768px) {
  .register-button {
    margin-bottom: 40px; /* Adjust as needed */
    position: relative;
    z-index: 1;
  }

  .footer {
    position: relative;
    z-index: 0;
  }
}
  `}</style>
        </Layout>
    );
};

export default CustomerRegisterWithReferral;
